import { batch } from 'react-redux';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  EntityId,
  unwrapResult,
} from '@reduxjs/toolkit';
import { deleteJSON, postJSON } from 'api/fetch';
import { captureException } from 'fe-core/util/errorTracking';
import { cloneDeep } from 'lodash';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { hideModal, showModal } from 'actions/modals';

import {
  getCurrentLocationId,
  getCurrentUserId,
  getCurrentUserIsManagerial,
} from 'selectors/session';

import { SHIFT_CONFLICT_MODAL } from 'features/modals/constants';
import { fetchSchedule } from 'features/scheduleBuilder/ScheduleBuilderView/actions/shared';
import {
  selectClosingTime,
  selectConflictsEnabled,
  selectIsJumpstartClosed,
  selectIsShiftsAtThisLocationOnly,
  selectJobIdsForCurrentLocation,
  selectJobIdsForUser,
  selectOpenTime,
  selectRoleByName,
  selectUserJobs,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';
import { OPEN_SHIFT_OPTION } from 'features/scheduleBuilder/ScheduleBuilderView/ShiftForm/constants';
import { selectClaimRequestsByShiftId } from 'features/scheduleBuilder/slices/claimRequests/slice';
import { selectShiftConflictType } from 'features/scheduleBuilder/slices/conflicts/slice';
import {
  actions as shiftNotesQuotaActions,
  selectShiftNotesQuotaAttribute,
} from 'features/scheduleBuilder/slices/shiftNotesQuotas/slice';
import { shiftSortCompare } from 'features/scheduleBuilder/slices/shifts/helpers';

import {
  addDays,
  clockFromTime,
  dateFromTime,
  datesInRange,
  endOfWeek,
  normalizeTimeString,
  startOfWeek,
} from 'util/dateTime';
import { toI18n } from 'util/i18n';

import { removeMany, selectAllConflicts } from '../conflicts/slice';
import { captureHydrationException, getSliceState } from '../helpers';
import { RootState } from '../scheduleBuilder';
import { ShiftNotesQuota } from '../shiftNotesQuotas/types';

import { updateShift } from './actions';
import { SLICE_NAME } from './constants';
import {
  Shift,
  ShiftAttributeValue,
  ShiftKeys,
  ShiftRelationshipKeys,
  ShiftsState,
} from './types';

export const selectShiftNotesQuotaData = createCachedSelector(
  (state: RootState, props: { shiftNoteQuotaId: string }) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'unlimited',
      id: props.shiftNoteQuotaId,
    }) || false,
  (state: RootState, props: { shiftNoteQuotaId: string }) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'used',
      id: props.shiftNoteQuotaId,
    }) || 0,
  (state: RootState, props: { shiftNoteQuotaId: string }) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'allowed',
      id: props.shiftNoteQuotaId,
    }) || 0,
  (unlimited: boolean, used: number, allowed: number) => ({
    unlimited,
    used,
    allowed,
  })
)(
  (_, props: { shiftNoteQuotaId: string }) =>
    `selectShiftNotesQuotaData-${props.shiftNoteQuotaId}`
);

export const selectIsShiftNoteAllowed = createCachedSelector(
  selectShiftNotesQuotaData,
  ({ unlimited, used, allowed }) => unlimited || used < allowed
)(
  (_, props: { shiftNoteQuotaId: string }) =>
    `selectIsShiftNoteAllowed-${props.shiftNoteQuotaId}`
);

export const selectNumLimitedNotesLeftInQuota = createCachedSelector(
  selectShiftNotesQuotaData,
  ({ unlimited, used, allowed }) =>
    unlimited || used > allowed ? 0 : allowed - used
)(
  (_, props: { shiftNoteQuotaId: string }) =>
    `selectNumLimitedNotesLeftInQuota-${props.shiftNoteQuotaId}`
);

export const selectHideWithNoConflicts = createCachedSelector(
  (state: RootState, props: { id: string }) =>
    selectShiftConflictType(state, props),
  (state: RootState) => selectConflictsEnabled(state),
  (conflictType: any, enabled: boolean) => enabled && !conflictType
)((_, props: { id: string }) => `selectHighlightConflict-${props.id}`);

export const selectAllLocationIdsForUser = createSelector(
  (state: RootState) => {
    const userId = getCurrentUserId(state);
    const jobs = selectUserJobs(state, userId);
    return jobs
      .map((job: any) => job.location?.id)
      .filter(Boolean)
      .join(',');
  },
  (ids: string) => (ids.length ? ids.split(',') : [])
);

export const selectShiftHasClaimRequests = createSelector(
  (state: RootState, { id }: { id: string }) =>
    selectClaimRequestsByShiftId(state, id),
  (claimRequests: any[]) => claimRequests?.length > 0
);

export const shiftsAdapter = createEntityAdapter<any>();

const initialState: ShiftsState = {
  ...shiftsAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
  pending: {
    stack: [],
    index: {},
  },
};

export const {
  selectAll: selectAllNonPendingShifts,
  selectById: selectShiftById,
  selectIds: selectShiftIds,
} = shiftsAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

const selectPendingEntityVersions = (
  pendingStack: string[],
  pendingIndex: any,
  type: string,
  id: string
) => {
  const filtered = pendingStack
    .map(pendingId => pendingIndex[pendingId]?.[type]?.[id])
    .filter(v => !!v);
  return filtered;
};

const mergeEntities = (entities: any[]) =>
  entities.reduce(
    (mergedEntity, pendingEntity) => ({
      ...mergedEntity,
      attributes: {
        ...(mergedEntity.attributes || {}),
        ...(pendingEntity.attributes || {}),
      },
      relationships: {
        ...(mergedEntity.relationships || {}),
        ...(pendingEntity.relationships || {}),
      },
      ...pendingEntity,
    }),
    {}
  );

export const selectShiftWithPending = createSelector(
  [
    (state: RootState, id: EntityId) => selectShiftById(state, id),
    (state: RootState) => {
      const sliceState = getSliceState(state)?.[SLICE_NAME] || initialState;
      return (
        sliceState.pending || {
          stack: [],
          index: {},
        }
      );
    },
    (state: RootState, id: EntityId) => id,
  ],
  (savedShift, pendingState, id) => {
    if (!savedShift && !pendingState.stack.length) return undefined;

    const pendingVersions = selectPendingEntityVersions(
      pendingState.stack,
      pendingState.index,
      'shift',
      String(id) || ''
    );

    if (!pendingVersions.length) return savedShift;

    const entities = savedShift
      ? [savedShift, ...pendingVersions]
      : pendingVersions;
    const merged = mergeEntities(entities);

    return {
      ...merged,
      _isPending: pendingVersions.length > 0,
    };
  }
);

export const calculateShiftStartEnd = ({
  date,
  start,
  end,
}: {
  date: string;
  start: string;
  end: string;
}) => {
  let endAt = `${date} ${end}`;

  if (normalizeTimeString(start) >= normalizeTimeString(end)) {
    const nextDate = addDays(date, 1);
    endAt = `${nextDate} ${end}`;
  }

  return {
    startAt: `${date} ${start}`,
    endAt,
  };
};

export const selectShiftAttribute = ({
  state,
  id,
  attribute,
}: {
  state: RootState;
  id: EntityId;
  attribute: keyof Shift;
}) => {
  const shift = selectShiftWithPending(state, id);
  return shift?.[attribute];
};

export const selectShiftRelationshipEntity = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: ShiftRelationshipKeys;
}):
  | { id: string; type: 'owner' | 'role' | 'shiftNote' | 'conflict' }
  | undefined => {
  const shift = selectShiftWithPending(state, id);
  if (!shift) return undefined;

  if (relationship === 'owner') {
    return shift.owner;
  }
  if (relationship === 'role') {
    return shift.role;
  }
  if (relationship === 'note') {
    return shift.note;
  }
  if (relationship === 'conflict') {
    return shift.conflict;
  }

  return undefined;
};

export const conflictCheck = createAsyncThunk(
  'shifts/conflictCheck',
  async ({ shifts }: { shifts: Shift[] }) => {
    const payload = {
      shifts: {
        data: shifts.map(shift => {
          const { id, owner, startAt, endAt } = shift;
          return {
            id,
            type: 'shift',
            attributes: {
              id,
              startAt,
              endAt,
            },
            relationships: {
              owner: {
                data: owner ? { id: owner.id, type: owner.type } : null,
              },
            },
          };
        }),
      },
    };
    return postJSON('/schedule_builder/shifts/conflict_check.json', payload);
  }
);

export const claimShift = createAsyncThunk(
  'shifts/claim',
  async (id: string, thunkAPI) => {
    try {
      const response = await postJSON(
        `/schedule_builder/shifts/${id}/claim_requests`
      );
      return response;
    } catch (error) {
      const { response } = error as { response: Response };
      const errorData = await response.json();
      return thunkAPI.rejectWithValue(
        errorData?.errors?.[0]?.detail ||
          toI18n('fe_design_base.errors.general')
      );
    }
  }
);

interface CreateShiftData {
  startAt?: string;
  endAt?: string;
  color?: string;
  roleName?: string | null;
  owner?: {
    id: string;
    type: 'job' | 'location';
  };
  role?: {
    id: string;
    type: 'role';
  };
  type?: string;
  note?: string;
}

interface CreateShiftPayload {
  id?: string;
  pendingId?: string;
  payload?: CreateShiftData;
  beforeDigestCallback?: () => void;
}

export const createShift = createAsyncThunk<Shift, CreateShiftPayload>(
  `${SLICE_NAME}/createShift`,
  async (params: CreateShiftPayload, { dispatch }) => {
    try {
      const shiftData = (params.payload || params) as CreateShiftData;
      const response = await postJSON(`/schedule_builder/shifts`, {
        data: {
          attributes: {
            startAt: shiftData.startAt,
            endAt: shiftData.endAt,
            color: shiftData.color,
            roleName: shiftData.roleName,
            note: shiftData.note,
          },
          relationships: {
            ...(shiftData.owner && {
              owner: {
                data: {
                  id: shiftData.owner?.id,
                  type: shiftData.owner?.type,
                },
              },
            }),
            ...(shiftData.role && {
              role: {
                data: {
                  id: shiftData?.role?.id,
                  type: shiftData?.role?.type,
                },
              },
            }),
          },
          type: 'shift',
        },
      });
      const includedEntities = (response?.included ?? []) as any[];

      const shiftNotesQuotas = includedEntities
        .filter(entity => entity.type === 'shiftNotesQuota')
        .map(entity => ({
          id: entity.id,
          type: entity.type,
          unlimited: entity.attributes.unlimited,
          used: entity.attributes.used,
          allowed: entity.attributes.allowed,
          month: entity.attributes.month,
          year: entity.attributes.year,
        }));

      if (shiftNotesQuotas.length > 0) {
        dispatch(
          shiftNotesQuotaActions.upsertMany(
            shiftNotesQuotas as ShiftNotesQuota[]
          )
        );
      }

      return response;
    } catch (error) {
      captureException(error);
    }
  }
);

export const showConflictModal = (props: any) =>
  showModal(SHIFT_CONFLICT_MODAL, props);

export const getShiftsForADay = ({
  date,
  from,
  to,
  roleId,
  ...otherShiftProps
}: {
  date: string;
  from: string;
  to: string;
  roleId?: string;
  [key: string]: any;
}) => {
  const { startAt, endAt } = calculateShiftStartEnd({
    date,
    start: from,
    end: to,
  });

  const shift: Partial<Shift> = {
    startAt,
    endAt,
    type: 'shift',
    ...otherShiftProps,
  };

  if (roleId) shift.role = { id: roleId, type: 'role' };

  return shift;
};

export const getShiftsForMultipleDays = ({
  dayOfWeekToDateMap,
  currentShiftDate,
  id,
  from,
  to,
  roleId,
  days,
  ...otherShiftProps
}: {
  dayOfWeekToDateMap: { [key: string]: string };
  currentShiftDate: string;
  id?: string;
  from: string;
  to: string;
  roleId?: string;
  days: string[];
  [key: string]: any;
}) =>
  days.map(day => {
    const currentDate = dayOfWeekToDateMap[day];
    const dayShiftId = currentShiftDate === currentDate ? id || null : null;

    return getShiftsForADay({
      from,
      to,
      roleId,
      id: dayShiftId,
      date: currentDate,
      ...otherShiftProps,
    });
  });

export const removeConflicts = (
  state: RootState,
  id: string,
  dispatch: any
) => {
  const allConflicts = selectAllConflicts(state);
  const conflictsToRemove = allConflicts
    .filter(conflict => conflict.sourceId === id || conflict.targetId === id)
    .map(conflict => conflict.id);

  if (conflictsToRemove.length > 0) {
    dispatch(removeMany(conflictsToRemove));
  }
};

const deleteShiftRequest = createAsyncThunk(
  `${SLICE_NAME}/deleteShiftRequest`,
  async ({ id }: { id: string }) => {
    try {
      const response = await deleteJSON(`/schedule_builder/shifts/${id}`);
      return response;
    } catch (error) {
      captureException(error);
    }
  }
);

export const deleteShift = createAsyncThunk(
  `${SLICE_NAME}/deleteShift`,
  async ({ id }: { id: string }, { dispatch, getState }) => {
    try {
      const state = getState() as RootState;
      const isManager = getCurrentUserIsManagerial(state);
      const affectsUnpublishedCounter = selectShiftAttribute({
        state,
        id,
        attribute: 'affectsUnpublishedCounter',
      });
      const entity = selectShiftRelationshipEntity({
        state,
        relationship: 'owner',
        id,
      });
      const ownerId = entity?.id;
      const ownerType = entity?.type as string;

      if (!id || id.startsWith('pending_')) {
        return;
      }

      await removeConflicts(state, id, dispatch);

      return dispatch(deleteShiftRequest({ id })).then((response: any) => {
        if (affectsUnpublishedCounter) {
          dispatch({
            type: 'schedule/incrementDeletedShiftsCount',
          });
        }

        if (ownerType === 'job' && isManager) {
          dispatch({
            type: 'scheduleBuilderView/updatePendingJobLaborTotalsIds',
            payload: ownerId,
          });
        }

        dispatch(
          shiftNotesQuotaActions.updateShiftNotesQuotas(response.payload)
        );
        return response;
      });
    } catch (error) {
      captureException(error);
    }
  }
);

const processShiftEntities = (included: any[]): any[] =>
  included
    .filter((entity: any) => entity.type === 'shift')
    .map((entity: any) => ({
      id: entity.id,
      type: entity.type,
      startAt: entity.attributes.startAt,
      endAt: entity.attributes.endAt,
      color: entity.attributes.color,
      roleName: entity.attributes.roleName,
      isOpen: entity.attributes.isOpen,
      isPublished: entity.attributes.isPublished,
      approvalEvent: entity.attributes.approvalEvent,
      isApproved: entity.attributes.isApproved,
      coverRequestIndicator: entity.attributes.coverRequestIndicator,
      tradeRequestIndicator: entity.attributes.tradeRequestIndicator,
      shiftClaimIndicator: entity.attributes.shiftClaimIndicator,
      hasTimecard: entity.attributes.hasTimecard,
      syncError: entity.attributes.syncError,
      hasNote: entity.attributes.hasNote,
      affectsUnpublishedCounter: entity.attributes.affectsUnpublishedCounter,
      note: entity.relationships?.note?.data || undefined,
      owner: entity.relationships?.owner?.data || undefined,
      role: entity.relationships?.role?.data || undefined,
      conflict: entity.relationships?.conflict?.data || undefined,
      shiftEvents: entity.relationships?.shiftEvents?.data || [],
      ...Object.fromEntries(
        Object.entries(entity.relationships || {}).map(
          ([k, v]: [string, any]) => [k, (v as any).data]
        )
      ),
    }));

const updateShiftsState = (state: any, included: any[]) => {
  const shiftEntities = processShiftEntities(included);
  if (shiftEntities.length > 0) {
    shiftsAdapter.upsertMany(state, shiftEntities);
  }
};

export const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    upsertOne: shiftsAdapter.upsertOne,
    setAll: shiftsAdapter.setAll,
    removeAll: shiftsAdapter.removeAll,
    removeShift: shiftsAdapter.removeOne,
    addPending: (state, action) => {
      const { pendingId, entities } = action.payload;
      const pendingState: { [type: string]: { [id: string]: any } } = {};

      entities.forEach((entity: any) => {
        const { type, id, ...data } = entity;
        pendingState[type] = pendingState[type] || {};
        pendingState[type][String(pendingId)] = {
          type,
          id: String(pendingId),
          _new: !id,
          ...data,
        };
      });

      state.pending.index[pendingId] = pendingState;
      state.pending.stack.push(pendingId);
    },
    clearPendingAction: (state, action) => {
      const pendingId = action.payload;
      const pendingStack = state.pending.stack;
      delete state.pending.index[pendingId];
      state.pending.stack = pendingStack.filter(
        (id: string) => id !== pendingId
      );
    },
  },
  extraReducers: builder => {
    builder.addCase(deleteShiftRequest.fulfilled, (state, action) => {
      const shiftId = action.meta?.arg?.id;
      shiftsAdapter.removeOne(state, shiftId);
    });
    builder.addCase(createShift.fulfilled, (state, action: any) => {
      const entity = action.payload?.data as any;

      const shift = {
        id: entity.id,
        type: entity.type,
        startAt: entity.attributes.startAt,
        endAt: entity.attributes.endAt,
        color: entity.attributes.color,
        roleName: entity.attributes.roleName,
        isOpen: entity.attributes.isOpen,
        isPublished: entity.attributes.isPublished,
        approvalEvent: entity.attributes.approvalEvent,
        affectsUnpublishedCounter: entity.attributes.affectsUnpublishedCounter,
        isApproved: entity.attributes.isApproved,
        hasTimecard: entity.attributes.hasTimecard,
        syncError: entity.attributes.syncError,
        hasNote: entity.attributes.hasNote,
        coverRequestIndicator: entity.attributes.coverRequestIndicator,
        note: entity.relationships?.note?.data || null,
        owner: entity.relationships?.owner?.data || null,
        role: entity.relationships?.role?.data || null,
        conflict: entity.relationships?.conflict?.data || null,
        shiftEvents: entity.relationships?.shiftEvents?.data || [],
      };

      shiftsAdapter.addOne(state, shift);
    });
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      try {
        state.status = 'succeeded';
        if (action.payload?.included) {
          updateShiftsState(state, action.payload.included);
        }
      } catch (error) {
        captureHydrationException(state, error, SLICE_NAME);
      }
    });
    builder.addCase(updateShift.fulfilled, (state, action) => {
      const updatedShift = action?.payload?.data;

      try {
        if (action.payload?.included) {
          updateShiftsState(state, action.payload.included);
        }
        if (updatedShift) {
          updateShiftsState(state, [updatedShift]);
        }
      } catch (error) {
        captureHydrationException(state, error, SLICE_NAME);
      }
    });
  },
});

export const selectShiftsStatus = (state: RootState) =>
  getSliceState(state)?.[SLICE_NAME]?.status || 'idle';

const { addPending, clearPendingAction } = slice.actions;

export const clearPending = createAsyncThunk(
  `${SLICE_NAME}/clearPending`,
  ({ pendingId }: { pendingId: string }, { dispatch }: any) => {
    dispatch(clearPendingAction(pendingId));
    return Promise.resolve({ pendingId });
  }
);

export const addPendingShifts = createAsyncThunk(
  `${SLICE_NAME}/addPendingShifts`,
  async ({ entities }: { entities: any[] }, { dispatch }: any) => {
    const rawShiftId = entities[0]?.id;
    const shiftId = rawShiftId?.startsWith('pending_')
      ? rawShiftId.split('_')[1]
      : rawShiftId;

    const pendingId = `pending_${shiftId}_${Date.now()}_${Math.random()}`;
    dispatch(addPending({ pendingId, entities }));
    return { pendingId, pendingEntities: entities };
  }
);

export const selectShiftTimeRange = createCachedSelector(
  (state: any, props: any) =>
    selectShiftAttribute({
      state,
      id: props.shiftId,
      attribute: 'startAt',
    }),
  (state: any, props: any) =>
    selectShiftAttribute({
      state,
      id: props.shiftId,
      attribute: 'endAt',
    }),
  (state: RootState) => selectOpenTime(state),
  (state: RootState) => selectClosingTime(state),
  (state: RootState, props: any) => props.startAt,
  (state: RootState, props: any) => props.endAt,
  (
    startAt: any,
    endAt: any,
    openTime: any,
    closingTime: any,
    initialStartAt: any,
    initialEndAt: any
  ) => {
    let from = startAt ? clockFromTime(startAt) : initialStartAt;
    if (from === undefined) {
      from = `${openTime}:00`;
    }

    let to = endAt ? clockFromTime(endAt) : initialEndAt;
    if (to === undefined) {
      to = `${closingTime}:00`;
    }

    return {
      from,
      to,
    };
  }
)((_, props: any) => `selectShiftTimeRange-${props.shiftId}`);

export const selectShiftRelationshipEntities = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: ShiftRelationshipKeys;
}): { id: string; type: 'shiftEvent' }[] | undefined | [] => {
  const shift = selectShiftById(state, id);
  if (!shift) return [];

  if (relationship === 'shiftEvents') {
    const shiftEvents = shift.shiftEvents;
    return Array.isArray(shiftEvents) ? shiftEvents : [];
  }

  return [];
};

export const selectShiftsFetchIsPending = (state: RootState) =>
  selectShiftsStatus(state) === 'pending';

export const upsertOneShift = createAsyncThunk(
  `${SLICE_NAME}/upsertOne`,
  async (payload: any) => {
    try {
      const resp = await postJSON(`/schedule_builder/shifts`, payload);
      return resp;
    } catch (error) {
      captureException(error);
    }
  }
);

export const { setAll, removeAll, upsertOne, removeShift } = slice.actions;
export const { reducer } = slice;

export const dispatchShiftUpdate = ({
  dispatch,
  state,
  shift,
  pendingId,
  onShiftAdded,
  note,
}: {
  dispatch: any;
  state: RootState;
  shift: any;
  pendingId: string;
  onShiftAdded?: any;
  note?: string;
}) => {
  const { id, ...payload } = shift;

  const originalId = id?.startsWith('pending_') ? id.split('_')[1] : id;

  const action = !originalId ? createShift : updateShift;

  const beforeDigestCallback = () => {
    if (originalId) {
      removeConflicts(state, originalId, dispatch);
    }
  };
  return dispatch(
    action({
      id: originalId || null,
      pendingId,
      payload,
      beforeDigestCallback,
    })
  )
    .then(unwrapResult)
    .then((response: any) => {
      batch(() => {
        // When we update a shift, we sometimes will create a brand new shift
        // and so we should remove the old one
        if (response?.data?.id !== id) {
          return dispatch(removeShift(id));
        }

        if (onShiftAdded) {
          onShiftAdded(
            response.data.id,
            note,
            response.data.relationships.owner.data.id,
            response.data.relationships.owner.data.type
          );
          dispatch(shiftNotesQuotaActions.updateShiftNotesQuotas(response));
        }
      });
    });
};

export const dispatchShiftUpdates = (
  dispatch: any,
  state: any,
  shifts: any[],
  pendingId: string,
  onShiftAdded?: any,
  note?: string
) => {
  let resultPromise;
  batch(
    () =>
      (resultPromise = Promise.all(
        shifts.map(shift =>
          dispatchShiftUpdate({
            dispatch,
            state,
            shift,
            pendingId,
            onShiftAdded,
            note,
          })
        )
      ))
  );
  return resultPromise;
};

export const updateShifts = createAsyncThunk(
  'scheduleBuilder/updateShift',
  (
    {
      onDone = () => {},
      onFailure = () => {},
      jobLaborTotalsId,
      onShiftAdded,
      payload: { owner, days, roleName, ...otherShiftProps },
    }: {
      onDone?: () => void;
      onFailure?: () => void;
      uxContext: any;
      jobLaborTotalsId: any;
      onShiftAdded: any;
      payload: { owner: any; days: any; roleName: any; [key: string]: any };
    },
    { dispatch, getState }: { dispatch: any; getState: any }
  ) => {
    const state = getState();
    const isOpenShifts =
      !owner || owner?.type === 'location' || owner === OPEN_SHIFT_OPTION;
    const shiftDay = otherShiftProps.currentShiftDate;
    const currentLocationId = getCurrentLocationId(state);

    const ownerRelationship = isOpenShifts
      ? { type: 'location', id: currentLocationId }
      : { type: 'job', id: owner?.id || owner };

    const dayOfWeekToDateMap = datesInRange(
      startOfWeek(shiftDay),
      endOfWeek(shiftDay)
    ).reduce((array, date) => {
      array.push(date);
      return array;
    }, []);

    const isJumpstartDrawerOpen = !selectIsJumpstartClosed(state);
    roleName = roleName?.toLowerCase() === 'uncategorized' ? null : roleName;
    const roleId = selectRoleByName(state, roleName)?.id;

    const shifts = !days
      ? [
          getShiftsForADay({
            date: otherShiftProps.date,
            from: otherShiftProps.from,
            to: otherShiftProps.to,
            roleId: roleId?.toString(),
            owner: ownerRelationship,
            roleName,
            ...otherShiftProps,
          }),
        ]
      : getShiftsForMultipleDays({
          dayOfWeekToDateMap,
          currentShiftDate: otherShiftProps.currentShiftDate,
          id: otherShiftProps.id,
          from: otherShiftProps.from,
          to: otherShiftProps.to,
          roleId: roleId?.toString(),
          days,
          owner: ownerRelationship,
          roleName,
          ...otherShiftProps,
        });

    return dispatch(addPendingShifts({ entities: shifts }))
      .then(unwrapResult)
      .then(
        ({
          pendingEntities,
          pendingId,
        }: {
          pendingEntities: any;
          pendingId: any;
        }) => {
          const pendingShifts = cloneDeep(pendingEntities).map(
            (shift: any) =>
              // if (!shift.id) shift.id = String(pendingId);
              shift
          );

          const completeShiftUpdates = (
            updateShiftResponse: any,
            shouldHideModal = false,
            shouldHideJumpstartDrawer = false
          ) =>
            updateShiftResponse
              .then(() => {
                if (getCurrentUserIsManagerial(state) && jobLaborTotalsId)
                  dispatch({
                    type: 'scheduleBuilderView/updatePendingJobLaborTotalsIds',
                    payload: jobLaborTotalsId,
                  });

                if (shouldHideModal) dispatch(hideModal());

                if (shouldHideJumpstartDrawer && isJumpstartDrawerOpen)
                  dispatch({
                    type: 'scheduleBuilderView/closeJumpstartDrawer',
                  });

                onDone();
              })
              .catch(() => onFailure())
              .finally(() => dispatch(clearPending({ pendingId })));

          if (!isOpenShifts) {
            return dispatch(conflictCheck({ shifts: pendingShifts }))
              .then(unwrapResult)
              .then((daysWithConflict: any) => {
                const conflicts = Object.values(daysWithConflict);

                return completeShiftUpdates(
                  dispatchShiftUpdates(
                    dispatch,
                    state,
                    pendingShifts,
                    pendingId,
                    onShiftAdded,
                    otherShiftProps.note
                  ),
                  conflicts.length > 0,
                  true
                );
              });
          }

          return completeShiftUpdates(
            dispatchShiftUpdates(
              dispatch,
              state,
              pendingShifts,
              pendingId,
              onShiftAdded,
              otherShiftProps.note
            ),
            false,
            true
          );
        }
      );
  }
);

/* Use this selectAll selector in order to recieve all shifts */
/* including optimistic artifical ones */
export const selectAllShifts = createSelector(
  [
    selectAllNonPendingShifts,
    (state: RootState) => {
      const sliceState = getSliceState(state)?.[SLICE_NAME] || initialState;
      return (
        sliceState.pending || {
          stack: [],
          index: {},
        }
      );
    },
  ],
  (savedShifts, pendingState) => {
    const shiftsMap = new Map();
    const pendingShiftIds = new Set();

    // First, collect all original shift IDs that have pending versions
    pendingState.stack.forEach((pendingId: string) => {
      const pendingData = pendingState.index[pendingId];
      if (pendingData?.shift) {
        Object.entries(pendingData.shift).forEach(() => {
          // Extract original shift ID from pending ID format:
          // pending_{shiftId}_{timestamp}_{random}
          if (pendingId.startsWith('pending_')) {
            const parts = pendingId.split('_');
            if (parts.length >= 2) {
              const originalShiftId = parts[1];
              pendingShiftIds.add(originalShiftId);
            }
          }
        });
      }
    });

    // Add saved shifts only if they don't have pending versions
    savedShifts.forEach((shift: any) => {
      if (!pendingShiftIds.has(shift.id)) {
        shiftsMap.set(shift.id, shift);
      }
    });

    // Add pending shifts (these replace any saved versions)
    pendingState.stack.forEach((pendingId: string) => {
      const pendingData = pendingState.index[pendingId];
      if (pendingData?.shift) {
        Object.entries(pendingData.shift).forEach(
          ([id, pendingShift]: [string, any]) => {
            // Find the original saved shift to merge with
            let originalShiftId: undefined | string;
            if (pendingId.startsWith('pending_')) {
              const parts = pendingId.split('_');
              if (parts.length >= 2) {
                originalShiftId = parts[1];
              }
            }

            const savedShift = originalShiftId
              ? savedShifts.find((s: any) => s.id === originalShiftId)
              : null;
            if (savedShift) {
              const merged = mergeEntities([savedShift, pendingShift]);
              shiftsMap.set(originalShiftId, { ...merged, _isPending: true });
            } else {
              shiftsMap.set(id, { ...pendingShift, _isPending: true });
            }
          }
        );
      }
    });
    return Array.from(shiftsMap.values());
  }
);

export const selectShiftWhereAttribute = createCachedSelector(
  [
    selectAllShifts,
    (
      _state: RootState,
      { attribute, value }: { attribute: ShiftKeys; value: ShiftAttributeValue }
    ) => ({
      attribute,
      value,
    }),
  ],
  (
    shifts: Shift[],
    { attribute, value }: { attribute: ShiftKeys; value: ShiftAttributeValue }
  ) => shifts.filter((shift: Shift) => shift[attribute] === value)
)(
  (_, params?: { attribute: ShiftKeys; value: ShiftAttributeValue }) =>
    `selectShiftsWhereAttribute-${params?.attribute || 'unknown'}-${
      params?.value || 'unknown'
    }`
);

export const selectAllShiftsOnDate = createCachedSelector(
  (state: RootState) => selectAllShifts(state),
  (state: RootState) => selectIsShiftsAtThisLocationOnly(state),
  (state: RootState, props: { jobId: string }) =>
    selectJobIdsForUser(state, props),
  (state: RootState) => selectJobIdsForCurrentLocation(state),
  (_, { date }: { date: string }) => date,
  (
    shifts: Shift[],
    isShiftsAtThisLocationOnly: boolean,
    jobIds: string[],
    jobIdsCurrentLocation: string[],
    date: string
  ) => {
    let filteredShifts = shifts.filter(
      s =>
        dateFromTime(s.startAt) === date &&
        s.owner.type === 'job' &&
        jobIds.includes(s.owner.id)
    );

    if (isShiftsAtThisLocationOnly) {
      filteredShifts = filteredShifts.filter(s =>
        jobIdsCurrentLocation.includes(s.owner.id)
      );
    }

    return filteredShifts.sort(shiftSortCompare);
  }
)(
  (_, { jobId, userId, date }: { jobId?: any; userId?: any; date: string }) =>
    `selectAllShiftsOnDate-${jobId}-${userId}-${date}`
);

export const selectShiftIdsForJobOnDate = createCachedSelector(
  (state: RootState, props: any) =>
    selectAllShiftsOnDate(state, props)
      .map(({ id }: { id: EntityId }) => id)
      .join(','),
  (ids: string) => (ids.length ? ids.split(',') : [])
)(
  (_, { userId, date }: { userId: string; date: string }) =>
    `selectShiftIdsForJobOnDate-${userId}-${date}`
);
